import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal } from "reactstrap";
import {
  ChevronRight,
  Download,
  Pencil,
  Plus,
  Search as SearchIcon,
  Tag,
  Trash2,
} from "lucide-react";
import * as XLSX from "xlsx";

import { deleteOffer, getOffer } from "../../../redux/offer/action";
import axiosInstance from "../../../redux/api";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import DeleteOffers from "./DeleteOffers";
import "../../Common/ListShell/ListShell.css";

const DEBOUNCE_MS = 400;

// Offer · coupon · usage · status · schedule · actions
const ROW_GRID = "minmax(0, 1.8fr) 140px 110px 120px 180px 92px";

const PLATFORM_LABELS = { 1: "App only", 2: "Web only" };

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value.split ? value.split("T")[0] : value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const OffersList = () => {
  const { hasAction } = usePermissions();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [params, setParams] = useState({
    page: 1,
    search_key: "",
    status: "",
    per_page: "10",
  });
  const [searchtext, setSearchtext] = useState("");
  const [modal, setModal] = useState(false);
  const [currentComponent, setCurrentComponent] = useState({});
  const [, setLoading] = useState(false);

  const offer = useSelector(({ offer: { offerlist } }) => offerlist);
  const offers = useMemo(() => offer?.offers || [], [offer]);
  const paginationMeta = offer?.pagination_meta;
  const totalCount = paginationMeta?.total ?? offers.length;

  const canDelete = hasAction(MENU.OFFERS, PERMISSION.DELETE);

  useEffect(() => {
    dispatch(getOffer(params));
  }, [dispatch, params]);

  useEffect(() => {
    const t = setTimeout(() => {
      setParams((prev) => ({ ...prev, search_key: searchtext, page: 1 }));
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchtext]);

  const handlePageChange = (page) => setParams((prev) => ({ ...prev, page }));

  const setStatusFilter = (next) =>
    setParams((prev) => ({ ...prev, status: next, page: 1 }));

  const deleteComponent = () => {
    if (currentComponent?.id) {
      dispatch(deleteOffer(currentComponent.id, setLoading));
    }
  };

  const handleExport = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post("offer/get_all", {
        ...params,
        page: "",
      });
      const data = response?.data?.response?.offers || [];
      if (!data.length) return;

      const header = [
        ["Name", "Description", "Status", "Times Used", "Usage Limit", "Channel", "Coupon Code", "Start Date", "End Date"],
      ];
      const body = data.map((item) => [
        item?.name || "-",
        item?.description || "-",
        item?.status === 1 ? "Active" : item?.status === 2 ? "Inactive" : "-",
        item?.used_count || 0,
        item?.usage_limit ?? "Unlimited",
        PLATFORM_LABELS[item?.platform_restriction] || "Everywhere",
        item?.coupon_code || "-",
        formatDate(item?.start_date),
        formatDate(item?.end_date),
      ]);

      const ws = XLSX.utils.aoa_to_sheet(header);
      XLSX.utils.sheet_add_aoa(ws, body, { origin: "A2" });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Offers");
      XLSX.writeFile(wb, "OffersList.xlsx");
    } catch (error) {
      console.error("Offer export error:", error);
    }
  };

  return (
    <div className="nl-page">
      {/* Crumb */}
      <div className="nl-topbar">
        <span className="nl-topbar__crumb">Marketing</span>
        <ChevronRight size={12} className="nl-topbar__sep" aria-hidden="true" />
        <span className="nl-topbar__current">Offers</span>
        <span className="nl-topbar__count">{totalCount.toLocaleString()}</span>
      </div>

      {/* Header band */}
      <header className="nl-header">
        <div className="nl-header__row">
          <div className="nl-header__title-block">
            <span className="nl-header__eyebrow">
              <Tag size={11} aria-hidden="true" />
              Marketing
            </span>
            <h1 className="nl-header__title">Offers</h1>
            <p className="nl-header__sub">
              Create and manage discounts, coupons, and promotional campaigns.
            </p>
          </div>
          <div className="nl-header__actions">
            <button
              type="button"
              className="nb-btn nb-btn--ghost"
              onClick={handleExport}
            >
              <Download size={14} aria-hidden="true" />
              Export CSV
            </button>
            <button
              type="button"
              className="nb-btn nb-btn--primary"
              onClick={() => navigate("/addoffers")}
            >
              <Plus size={14} aria-hidden="true" />
              New offer
            </button>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="nl-toolbar">
        <div className="nl-search">
          <SearchIcon size={14} className="nl-search__icon" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search offers…"
            value={searchtext}
            onChange={(e) => setSearchtext(e.target.value)}
          />
          {searchtext && (
            <button
              type="button"
              className="nl-search__clear"
              onClick={() => setSearchtext("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="nl-chips">
          <span className="nl-chiplabel">Status</span>
          <button
            type="button"
            className={`nl-chipfilter ${params.status === "" ? "is-active" : ""}`}
            onClick={() => setStatusFilter("")}
          >
            <span className="nl-chipfilter__dot nl-chipfilter__dot--neutral" />
            All
          </button>
          <button
            type="button"
            className={`nl-chipfilter ${params.status === "1" ? "is-active" : ""}`}
            onClick={() => setStatusFilter("1")}
          >
            <span className="nl-chipfilter__dot nl-chipfilter__dot--on" />
            Active
          </button>
          <button
            type="button"
            className={`nl-chipfilter ${params.status === "2" ? "is-active" : ""}`}
            onClick={() => setStatusFilter("2")}
          >
            <span className="nl-chipfilter__dot nl-chipfilter__dot--off" />
            Inactive
          </button>
        </div>

        <div className="nl-toolbar__spacer" />
      </div>

      {/* Table */}
      <div className="nl-tablewrap">
        <div className="nl-thead" style={{ gridTemplateColumns: ROW_GRID }}>
          <span>Offer</span>
          <span>Coupon</span>
          <span>Usage</span>
          <span>Status</span>
          <span>Schedule</span>
          <span />
        </div>

        {offers.length > 0 ? (
          <div className="nl-tbody">
            {offers.map((item) => {
              const isActive = item.status === 1;
              return (
                <div
                  key={item.id}
                  className="nl-row"
                  style={{ gridTemplateColumns: ROW_GRID }}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/updateoffers/${item.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/updateoffers/${item.id}`);
                  }}
                >
                  <div className="nl-cell-brand">
                    <div className={`nl-avatar ${item.image ? "" : "is-empty"}`}>
                      {item.image ? (
                        <img src={item.image} alt={item.name} />
                      ) : (
                        <span>{(item.name || "?").charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="nl-cell-meta">
                      <div className="nl-cell-meta__title">
                        <strong>{item.name}</strong>
                      </div>
                      {item.description && (
                        <div className="nl-cell-meta__sub">{item.description}</div>
                      )}
                    </div>
                  </div>

                  <div className="nl-cell">
                    {item.coupon_code ? (
                      // Left-aligned column; nl-cell-meta__sub is not used here
                      // because it is RTL/right-aligned (built for Arabic subs).
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                          alignItems: "flex-start",
                        }}
                      >
                        <span className="nl-pos">{item.coupon_code}</span>
                        {PLATFORM_LABELS[item.platform_restriction] && (
                          <span
                            style={{
                              fontSize: 10.5,
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                              color: "#4f46e5",
                              background: "#eef2ff",
                              padding: "1px 7px",
                              borderRadius: 999,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {PLATFORM_LABELS[item.platform_restriction]}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="nl-cell--muted">—</span>
                    )}
                  </div>

                  <div className="nl-cell" style={{ whiteSpace: "nowrap" }}>
                    {`${item.used_count ?? 0} / ${item.usage_limit ?? "∞"}`}
                  </div>

                  <div className="nl-cell">
                    <span
                      className={`nl-badge ${
                        isActive ? "nl-badge--on" : "nl-badge--off"
                      }`}
                    >
                      <span className="nl-badge__dot" aria-hidden="true" />
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="nl-cell nl-cell--date">
                    <span>{formatDate(item.start_date)}</span>
                    <span>→ {formatDate(item.end_date)}</span>
                  </div>

                  <div className="nl-rowactions">
                    <button
                      type="button"
                      className="nl-iconbtn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/updateoffers/${item.id}`);
                      }}
                      aria-label="Edit"
                      title="Edit"
                    >
                      <Pencil size={14} aria-hidden="true" />
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        className="nl-iconbtn nl-iconbtn--danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setModal(true);
                          setCurrentComponent(item);
                        }}
                        aria-label="Delete"
                        title="Delete"
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="nl-empty">
            <span className="nl-empty__art">
              <Tag size={26} aria-hidden="true" />
            </span>
            <h3 className="nl-empty__title">No offers yet</h3>
            <p className="nl-empty__sub">
              {searchtext
                ? `No offers match "${searchtext}"`
                : "Create your first offer to start running promotions."}
            </p>
            {!searchtext && (
              <button
                type="button"
                className="nb-btn nb-btn--primary"
                onClick={() => navigate("/addoffers")}
              >
                <Plus size={14} aria-hidden="true" />
                New offer
              </button>
            )}
          </div>
        )}
      </div>

      {offers.length > 0 && (
        <div className="nl-paginate">
          <div className="nl-perpage">
            <span>Show</span>
            <select
              className="nl-perpage__select"
              value={params.per_page}
              onChange={(e) =>
                setParams((prev) => ({
                  ...prev,
                  per_page: e.target.value,
                  page: 1,
                }))
              }
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span>per page</span>
          </div>
          <CommonPagination
            currentPage={params.page}
            totalPages={paginationMeta?.pages || 1}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      <Modal isOpen={modal} toggle={() => setModal((s) => !s)}>
        <DeleteOffers
          setModal={setModal}
          currentComponent={currentComponent}
          deleteComponent={deleteComponent}
        />
      </Modal>
    </div>
  );
};

export default OffersList;
